'use client';

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { proxyFetch } from '@ps/functions/utils/proxyFetch';
import { useCurrentUserId } from '@ps/ui/contexts/auth';

import type { LeadFormGuardItem } from './hooks/use-lead-form-guard';

// ─── Context ─────────────────────────────────────────────────────────────

interface LeadFormGuardContextValue {
  leadForms: LeadFormGuardItem[];
  isLoaded: boolean;
  /** Find a lead form that gates the given triggers (first match wins). */
  getGuard(
    triggers: Array<{ type: string; id: string }>,
  ): LeadFormGuardItem | null;
  /** Re-fetch active lead forms from the API. */
  refresh(): void;

  // ── Submission tracking (shared across all AccessGuard instances) ──

  /** True while past submission history is being fetched. */
  isLoadingSubmissions: boolean;
  /** Check if user already qualified for a specific lead form. */
  hasQualifiedForForm(formId: string): boolean;
  /** Optimistically mark a form as qualified (after modal completion). */
  markFormQualified(formId: string): void;
  /** Check if user submitted but did NOT qualify for a lead form. */
  isFormUnqualified(formId: string): boolean;
  /** Mark a form as submitted-but-unqualified (blocks future attempts). */
  markFormUnqualified(formId: string): void;
  /** Check if anonymous user already submitted contact form for this creator. */
  hasSubmittedContactForm(creatorId: string): boolean;
  /** Mark contact form as submitted for a creator (session-only). */
  markContactFormSubmitted(creatorId: string): void;
}

const LeadFormGuardContext = createContext<LeadFormGuardContextValue | null>(
  null,
);

// ─── Provider ────────────────────────────────────────────────────────────

/**
 * Provides active lead forms for a store to all descendants.
 * Also tracks past submission state (shared across all AccessGuard instances).
 *
 * Mirrors the RN pattern where `useLeadForms` is called in the bio provider
 * and the forms list + submission state are shared via context.
 */
export function LeadFormGuardProvider({
  storeId,
  children,
}: {
  storeId: string | undefined;
  children: ReactNode;
}) {
  // Viewer id from the bio's `AuthProvider` (firebase-free at the static import
  // level — the provider dynamic-imports the SDK). `undefined` while auth
  // resolves; `null` once resolved-anonymous — downstream reads (`!userId`)
  // treat both as logged-out.
  const userId = useCurrentUserId();
  const [leadForms, setLeadForms] = useState<LeadFormGuardItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // ── Submission state (shared across all guards) ──
  const [qualifiedFormIds, setQualifiedFormIds] = useState(
    () => new Set<string>(),
  );
  const [unqualifiedFormIds, setUnqualifiedFormIds] = useState(
    () => new Set<string>(),
  );
  const [contactSubmittedCreators] = useState(() => new Set<string>());
  const [isLoadingSubmissions, setIsLoadingSubmissions] = useState(false);
  const submissionsFetchedRef = useRef(false);

  function fetchForms() {
    if (!storeId) {
      setIsLoaded(true);
      return;
    }

    const params = new URLSearchParams({ creatorId: storeId });
    proxyFetch<LeadFormGuardItem[]>(`/api/bio/lead-forms?${params.toString()}`)
      .then(rows => {
        setLeadForms(rows);
        setIsLoaded(true);
      })
      .catch(err => {
        console.error(
          '[LeadFormGuardProvider] Failed to fetch lead forms:',
          err,
        );
        setLeadForms([]);
        setIsLoaded(true);
      });
  }

  useEffect(() => {
    fetchForms();
  }, [storeId]);

  // Fetch past lead form submissions for logged-in users (once per store)
  useEffect(() => {
    if (
      !userId ||
      !storeId ||
      leadForms.length === 0 ||
      submissionsFetchedRef.current
    )
      return;

    const leadFormIds = leadForms.map(f => f.id);
    let cancelled = false;
    submissionsFetchedRef.current = true;
    setIsLoadingSubmissions(true);

    const params = new URLSearchParams({
      creatorId: storeId,
      leadFormIds: leadFormIds.join(','),
    });
    proxyFetch<
      Array<{
        relations?: { parentId?: string };
        answers: Array<{
          options: Array<{ ideal?: boolean; selected?: boolean }>;
        }>;
      }>
    >(`/api/bio/lead-forms/submissions?${params.toString()}`)
      .then(submissions => {
        if (cancelled) return;

        const formsMap = new Map(leadForms.map(f => [f.id, f]));
        const qualifiedIds = new Set<string>();
        const unqualifiedIds = new Set<string>();

        for (const sub of submissions) {
          const formId = sub.relations?.parentId;
          if (!formId || !leadFormIds.includes(formId)) continue;

          const form = formsMap.get(formId);
          if (!form) {
            qualifiedIds.add(formId);
            continue;
          }

          const minRequired = form.minIdealAnswersRequired ?? 0;
          if (minRequired <= 0) {
            qualifiedIds.add(formId);
            continue;
          }

          let idealCount = 0;
          for (const questionAnswer of sub.answers) {
            const hasAnyIdeal = questionAnswer.options.some(o => o.ideal);
            if (!hasAnyIdeal) continue;
            if (questionAnswer.options.some(o => o.ideal && o.selected))
              idealCount++;
          }

          if (idealCount >= minRequired) {
            qualifiedIds.add(formId);
          } else {
            unqualifiedIds.add(formId);
          }
        }

        setQualifiedFormIds(qualifiedIds);
        setUnqualifiedFormIds(unqualifiedIds);
      })
      .catch(() => {
        // Ignore — anonymous users or network failures
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSubmissions(false);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, storeId, leadForms]);

  function getGuard(
    triggers: Array<{ type: string; id: string }>,
  ): LeadFormGuardItem | null {
    if (leadForms.length === 0 || triggers.length === 0) return null;

    for (const trigger of triggers) {
      const key = `${trigger.type}_${trigger.id}`;
      const match = leadForms.find(form =>
        form.triggerActionKeys?.includes(key),
      );
      if (match) return match;
    }

    return null;
  }

  function refresh() {
    setIsLoaded(false);
    fetchForms();
  }

  function hasQualifiedForForm(formId: string): boolean {
    return qualifiedFormIds.has(formId);
  }

  function markFormQualified(formId: string) {
    setQualifiedFormIds(prev => {
      const next = new Set(prev);
      next.add(formId);
      return next;
    });
    setUnqualifiedFormIds(prev => {
      if (!prev.has(formId)) return prev;
      const next = new Set(prev);
      next.delete(formId);
      return next;
    });
  }

  function isFormUnqualified(formId: string): boolean {
    return unqualifiedFormIds.has(formId);
  }

  function markFormUnqualified(formId: string) {
    setUnqualifiedFormIds(prev => {
      const next = new Set(prev);
      next.add(formId);
      return next;
    });
  }

  function hasSubmittedContactForm(creatorId: string): boolean {
    return contactSubmittedCreators.has(creatorId);
  }

  function markContactFormSubmitted(creatorId: string) {
    contactSubmittedCreators.add(creatorId);
  }

  return (
    <LeadFormGuardContext.Provider
      value={{
        leadForms,
        isLoaded,
        getGuard,
        refresh,
        isLoadingSubmissions,
        hasQualifiedForForm,
        markFormQualified,
        isFormUnqualified,
        markFormUnqualified,
        hasSubmittedContactForm,
        markContactFormSubmitted,
      }}
    >
      {children}
    </LeadFormGuardContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────

const defaultValue: LeadFormGuardContextValue = {
  leadForms: [],
  isLoaded: true,
  getGuard: () => null,
  refresh: () => {},
  isLoadingSubmissions: false,
  hasQualifiedForForm: () => false,
  markFormQualified: () => {},
  isFormUnqualified: () => false,
  markFormUnqualified: () => {},
  hasSubmittedContactForm: () => false,
  markContactFormSubmitted: () => {},
};

/**
 * Consume lead form guard data from the nearest `LeadFormGuardProvider`.
 * Returns a no-op default when used outside a provider (e.g. editor preview).
 */
export function useLeadFormGuardContext(): LeadFormGuardContextValue {
  return useContext(LeadFormGuardContext) ?? defaultValue;
}
